import { api } from './api.js';
import { load, save } from './storage.js';
import { fotosErsetzen, fotosFreigeben, fotosHochladen } from './fotospeicher.js';

/**
 * Offline-Formulare (Übersichtsliste, Messprotokoll, Abnahme …): Der Entwurf eines Formulars liegt bei jeder Änderung
 * sofort auf dem Gerät (useExcelEntwurf). Tippt man ohne Netz auf «Speichern», wird er «vorgemerkt» – mit Ziel
 * (Route, Auftrag, Datei) und festem Idempotency-Key – und hier automatisch gespeichert, sobald wieder Netz da ist:
 * beim Start, beim Wiederverbinden, nach der Anmeldung und alle 30 s, solange etwas wartet.
 *
 * Entwurf (localStorage «bs.<prefix>.<nr>.<dateiId>»):
 *   { daten, version, ziel: { art, nr, dateiId }, vorgemerkt?: { key, am }, fehler?: string }
 *
 * Wird der Entwurf nach dem Vormerken weiter bearbeitet, bekommt er einen neuen Key (sonst lieferte die API für den
 * alten Key die Antwort der früheren Sendung). Ändert er sich, während eine Sendung läuft, bleibt der neuere Stand
 * erhalten (mit der neuen Version vom Server) und wird in der nächsten Runde gespeichert.
 */

const PREFIX = 'bs.';
const listeners = new Set();
let laeuft = null;

/** Benachrichtigung an offene Editoren: { key, art: 'gespeichert'|'fehler'|'geaendert', datei?, neuerKey? } */
export const onEntwurf = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const melden = (e) => { for (const l of listeners) l(e); };

/** Schlüssel (ohne «bs.») aller vorgemerkten Entwürfe auf dem Gerät. */
export function vorgemerkteEntwuerfe() {
  const out = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k?.startsWith(PREFIX)) continue;
      const raw = window.localStorage.getItem(k);
      if (!raw || !raw.startsWith('{"daten"') || !raw.includes('"vorgemerkt":{')) continue;
      const e = JSON.parse(raw);
      if (e?.vorgemerkt?.key && e.ziel?.art && e.ziel?.nr) out.push({ key: k.slice(PREFIX.length), entwurf: e });
    }
  } catch { /* Speicher gesperrt */ }
  return out.sort((a, b) => String(a.entwurf.vorgemerkt.am).localeCompare(String(b.entwurf.vorgemerkt.am)));
}

/** Fehler, bei denen ein späterer Versuch sinnvoll (und dank Key gefahrlos) ist. */
const spaeter = (err) => err?.status === 0 || err?.status === 401 || err?.status === 429 || err?.status >= 500 || err?.code === 'IN_PROGRESS';
const gleich = (a, b) => JSON.stringify(a) === JSON.stringify(b);

async function senden({ key, entwurf }) {
  const { art, nr, dateiId } = entwurf.ziel;
  const enc = encodeURIComponent;
  const neu = dateiId === 'neu';
  // Fotos zuerst in den Auftrag (wirft ohne Netz → Formular wartet weiter); Ergebnis gleich im Entwurf festhalten
  const fotos = await fotosHochladen(nr, entwurf.daten);
  const e = { ...entwurf, daten: fotos.daten };
  if (fotos.ersetzt.size) {
    const stand = load(key, null);
    if (stand) save(key, { ...stand, daten: fotosErsetzen(stand.daten, fotos.ersetzt) });
    melden({ key, art: 'geaendert' });
    await fotosFreigeben(fotos.ersetzt);
  }
  const r = await api(neu ? `/auftraege/${enc(nr)}/${art}` : `/auftraege/${enc(nr)}/${art}/${enc(dateiId)}`, {
    method: neu ? 'POST' : 'PUT',
    body: neu ? { daten: e.daten } : { daten: e.daten, version: e.version },
    idempotencyKey: e.vorgemerkt.key,
  });
  const jetzt = load(key, null);
  const neuerKey = neu ? key.replace(/\.neu$/, `.${r.datei.id}`) : key;
  if (!jetzt || gleich(jetzt.daten, e.daten)) {
    save(key, null);
  } else {
    // während der Sendung weiter bearbeitet: neuerer Stand bleibt, jetzt auf der gespeicherten Datei
    save(key, null);
    save(neuerKey, { ...jetzt, version: r.version, ziel: { art, nr, dateiId: r.datei.id } });
  }
  melden({ key, art: 'gespeichert', ergebnis: r, neuerKey, ziel: e.ziel });
  return r;
}

/**
 * Alle vorgemerkten Entwürfe speichern (einer nach dem anderen). Bricht bei «kein Netz» ab.
 * @returns {Promise<{ gespeichert: object[], fehler: object[], wartet: number }>}
 */
export function entwuerfeSenden() {
  if (laeuft) return laeuft;
  laeuft = (async () => {
    const ergebnis = { gespeichert: [], fehler: [], wartet: 0 };
    const liste = vorgemerkteEntwuerfe();
    for (let i = 0; i < liste.length; i++) {
      const item = liste[i];
      try {
        const r = await senden(item);
        ergebnis.gespeichert.push({ ...item, ergebnis: r });
      } catch (err) {
        if (spaeter(err)) { ergebnis.wartet = liste.length - i; break; }
        // Konflikt (inzwischen geändert), ungültig, Auftrag weg: nicht endlos wiederholen – im Editor zeigen
        const jetzt = load(item.key, null) ?? item.entwurf;
        save(item.key, { ...jetzt, vorgemerkt: null, fehler: err.message });
        ergebnis.fehler.push({ ...item, fehler: err.message, code: err.code });
        melden({ key: item.key, art: 'fehler', fehler: err.message, code: err.code });
      }
    }
    return ergebnis;
  })().finally(() => { laeuft = null; });
  return laeuft;
}

/** Entwurf vormerken (ohne Netz «Speichern»). */
export function vormerken(key, entwurf, ziel, idempotencyKey) {
  const e = { ...entwurf, ziel, vorgemerkt: { key: idempotencyKey, am: new Date().toISOString() }, fehler: null };
  save(key, e);
  melden({ key, art: 'geaendert' });
  return e;
}
